<template>
  <div class="sp-page mx-auto">
    <div class="sp-head">
      <div>
        <h5 class="sp-title">
          <i class="bi bi-file-earmark-arrow-down"></i> Job Order Referrals
        </h5>
        <p class="sp-subtitle">
          Tickets the support team passed to General Services. Mark a referral
          completed once the job is done; its ticket closes and the requester is
          notified.
        </p>
      </div>
    </div>

    <CCard v-if="forbidden" class="sp-card shadow-sm">
      <CCardBody class="sp-state">
        <i class="bi bi-shield-lock"></i>
        <div class="sp-state-title">This page is for General Services</div>
        <div>{{ forbidden }}</div>
        <router-link to="/support/tickets" class="btn btn-link">
          Go to support tickets
        </router-link>
      </CCardBody>
    </CCard>

    <CCard v-else class="sp-card shadow-sm">
      <CCardBody class="p-0">
        <div class="sp-toolbar">
          <div class="sp-tabs" role="tablist" aria-label="Filter referrals by status">
            <button
              v-for="f in filters"
              :key="f.key"
              type="button"
              role="tab"
              class="sp-tab"
              :class="{ active: filter === f.key }"
              :aria-selected="filter === f.key ? 'true' : 'false'"
              @click="setFilter(f.key)"
            >
              {{ f.label }}
              <span class="sp-tab-count">{{ counts[f.key] || 0 }}</span>
            </button>
          </div>
        </div>

        <div class="sp-controls">
          <div class="sp-search">
            <i class="bi bi-search" aria-hidden="true"></i>
            <input
              v-model="search"
              type="search"
              class="form-control"
              placeholder="Search referral or ticket no., request, unit, or name"
              aria-label="Search referrals"
            />
          </div>
          <div class="sp-controls-right">
            <button
              type="button"
              class="btn btn-outline-secondary text-dark"
              title="Refresh"
              aria-label="Refresh"
              :disabled="refreshing"
              @click="refresh()"
            >
              <i class="bi bi-arrow-repeat" :class="{ 'sp-spin': refreshing }"></i>
            </button>
          </div>
        </div>

        <div v-if="selected.length" class="sp-selection">
          <span>
            <strong>{{ selected.length }}</strong>
            {{ selected.length === 1 ? "referral" : "referrals" }} selected
          </span>
          <button
            type="button"
            class="btn btn-sm btn-outline-success"
            :disabled="busy"
            @click="completeSelected"
          >
            <span v-if="busy" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="bi bi-check2-circle mr-1"></i>
            Mark as completed
          </button>
          <button type="button" class="btn btn-sm btn-link" @click="selected = []">
            Clear
          </button>
        </div>

        <div v-if="loadError" class="sp-error" role="alert">
          <i class="bi bi-exclamation-triangle"></i> {{ loadError }}
          <button type="button" class="btn btn-link" @click="refresh()">Try again</button>
        </div>

        <div class="table-responsive">
          <table class="table sp-table" :class="{ dim: refreshing && !loading }">
            <thead>
              <tr>
                <th class="sp-check">
                  <input
                    ref="selectAll"
                    type="checkbox"
                    :checked="allSelected"
                    :disabled="!selectableRows.length"
                    aria-label="Select all referrals on this page"
                    @change="toggleAll($event.target.checked)"
                  />
                </th>
                <th scope="col">Referral</th>
                <th scope="col">Ticket</th>
                <th scope="col">Request</th>
                <th scope="col">Unit</th>
                <th scope="col">Requested by</th>
                <th scope="col">Referred</th>
                <th scope="col">Status</th>
                <th scope="col"><span class="sr-only">Print</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="9" class="sp-state">
                  <span class="spinner-border spinner-border-sm mr-1"></span>
                  Loading referrals…
                </td>
              </tr>
              <tr v-else-if="!referrals.length">
                <td colspan="9" class="sp-state">
                  <i class="bi" :class="search ? 'bi-search' : 'bi-inbox'"></i>
                  <div class="sp-state-title">{{ emptyTitle }}</div>
                  <div v-if="search">Try a referral or ticket number, a unit, or a name.</div>
                  <button v-if="search" type="button" class="btn btn-link" @click="search = ''">
                    Clear search
                  </button>
                  <button
                    v-else-if="filter !== 'all' && counts.all"
                    type="button"
                    class="btn btn-link"
                    @click="setFilter('all')"
                  >
                    Show all referrals
                  </button>
                </td>
              </tr>
              <template v-else>
                <tr
                  v-for="referral in referrals"
                  :key="referral.id"
                  class="sp-row"
                  :class="{ selected: isSelected(referral) }"
                  tabindex="0"
                  :aria-label="`Referral ${referral.id} for ticket ${referral.ticket_id}: ${referral.type}`"
                  @click="openReferral(referral)"
                  @keydown.enter.self="openReferral(referral)"
                >
                  <td class="sp-check" @click.stop>
                    <input
                      v-if="canSelect(referral)"
                      type="checkbox"
                      :checked="isSelected(referral)"
                      :aria-label="`Select referral ${referral.id}`"
                      @change="toggleOne(referral, $event.target.checked)"
                    />
                  </td>
                  <td class="sp-id">#{{ referral.id }}</td>
                  <td class="sp-nowrap">#{{ referral.ticket_id }}</td>
                  <td class="sp-request">
                    <div class="sp-request-type">{{ referral.type }}</div>
                    <div class="sp-excerpt" :title="referral.description">
                      {{ referral.description }}
                    </div>
                  </td>
                  <td class="sp-nowrap">{{ unitLabel(referral.unit) }}</td>
                  <td class="sp-nowrap">{{ referral.creator_name || "—" }}</td>
                  <td>
                    <div class="sp-nowrap">{{ formatDate(referral.created_at) }}</div>
                    <div
                      v-if="referral.status !== 'Closed'"
                      class="sp-sub"
                      :class="{ late: isWaitingLong(referral) }"
                    >
                      waiting {{ shortAge(referral.created_at) }}
                    </div>
                  </td>
                  <td>
                    <span class="sp-pill" :style="pillStyle(statusMeta(referral.status))">
                      <i class="bi" :class="statusMeta(referral.status).icon"></i>
                      {{ statusMeta(referral.status).label }}
                    </span>
                    <div v-if="referral.closed_at" class="sp-sub mt-1">
                      {{ formatDate(referral.closed_at) }}
                    </div>
                  </td>
                  <td class="text-right" @click.stop>
                    <button
                      type="button"
                      class="btn btn-link sp-print"
                      :title="`Print job order request for referral ${referral.id}`"
                      :aria-label="`Print job order request for referral ${referral.id}`"
                      @click="print(referral)"
                    >
                      <i class="bi bi-printer"></i>
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="sp-footer">
          <div class="sp-rows">
            <label for="support-referral-rows">Rows</label>
            <select
              id="support-referral-rows"
              v-model.number="perPage"
              class="form-control form-control-sm"
              @change="changePerPage"
            >
              <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
          <div class="sp-pager">
            <span>{{ rangeLabel }}</span>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary text-dark"
              aria-label="Previous page"
              :disabled="pagination.current_page <= 1 || refreshing"
              @click="goToPage(pagination.current_page - 1)"
            >
              <i class="bi bi-chevron-left"></i>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary text-dark"
              aria-label="Next page"
              :disabled="pagination.current_page >= pagination.last_page || refreshing"
              @click="goToPage(pagination.current_page + 1)"
            >
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <JobOrderRequestTemplate
      v-show="false"
      ref="JobOrderRequestTemplate"
    ></JobOrderRequestTemplate>

    <ViewJobOrderReferralDetailsModal
      ref="ViewJobOrderReferralDetailsModal"
      @changed="refresh"
      @print="print"
      @hidden="clearReferralQuery"
    ></ViewJobOrderReferralDetailsModal>
  </div>
</template>

<script>
import "../support.css";
import JobOrderRequestTemplate from "./components/JobOrderRequestTemplate.vue";
import ViewJobOrderReferralDetailsModal from "./components/ViewJobOrderReferralDetailsModal.vue";
import {
  REFERRAL_FILTERS,
  UNITS,
  daysSince,
  errorMessage,
  formatDate,
  pillStyle,
  referralStatusMeta,
  shortAge,
} from "../supportOptions";

const WAITING_LONG_DAYS = 7;

export default {
  name: "JobOrderReferrals",
  components: {
    JobOrderRequestTemplate,
    ViewJobOrderReferralDetailsModal,
  },
  data() {
    return {
      referrals: [],
      counts: {},
      filters: REFERRAL_FILTERS,
      filter: "ongoing",
      search: "",
      perPage: 10,
      perPageOptions: [10, 25, 50, 100],
      pagination: { current_page: 1, last_page: 1, from: 0, to: 0, total: 0 },
      loading: true,
      refreshing: false,
      loadError: "",
      forbidden: "",
      selected: [],
      busy: false,
      firstLoad: true,
      requestSeq: 0,
      searchTimer: null,
    };
  },
  computed: {
    selectableRows() {
      return this.referrals.filter((referral) => this.canSelect(referral));
    },
    allSelected() {
      return (
        this.selectableRows.length > 0 &&
        this.selectableRows.every((referral) => this.isSelected(referral))
      );
    },
    someSelected() {
      return this.selected.length > 0 && !this.allSelected;
    },
    rangeLabel() {
      const p = this.pagination;
      if (!p.total) return "0 referrals";
      return `${p.from}–${p.to} of ${p.total}`;
    },
    emptyTitle() {
      if (this.search) return "No referrals match your search";
      if (!this.counts.all) return "No job order referrals yet";
      if (this.filter === "ongoing") return "No ongoing referrals — all caught up";
      if (this.filter === "completed") return "No completed referrals yet";
      return "No referrals";
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.pagination.current_page = 1;
        this.fetchReferrals();
      }, 350);
    },
    someSelected: {
      handler(value) {
        this.$nextTick(() => {
          if (this.$refs.selectAll) this.$refs.selectAll.indeterminate = value;
        });
      },
      immediate: true,
    },
    "$route.query.referral"() {
      this.openFromRoute();
    },
    "$route.query.ticket"() {
      this.openFromRoute();
    },
  },
  mounted() {
    this.fetchReferrals().then(() => this.openFromRoute());
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    statusMeta: referralStatusMeta,
    pillStyle,
    shortAge,
    formatDate,
    unitLabel(value) {
      const unit = UNITS.find((u) => u.value === value);
      return unit ? unit.label : value || "—";
    },
    fetchReferrals({ keepSelection = false } = {}) {
      const seq = ++this.requestSeq;
      this.refreshing = true;

      return axios
        .get("api/support/job-order-referrals", {
          params: {
            status: this.filter,
            search: this.search.trim() || undefined,
            perPage: this.perPage,
            page: this.pagination.current_page || 1,
          },
        })
        .then((res) => {
          if (seq !== this.requestSeq) return null;
          const data = res.data || {};

          if (
            this.firstLoad &&
            this.filter === "ongoing" &&
            data.counts &&
            !data.counts.ongoing &&
            data.counts.all
          ) {
            this.firstLoad = false;
            this.filter = "all";
            return this.fetchReferrals();
          }
          this.firstLoad = false;

          this.referrals = data.data || [];
          this.counts = data.counts || {};
          this.pagination = {
            current_page: data.current_page || 1,
            last_page: data.last_page || 1,
            from: data.from || 0,
            to: data.to || 0,
            total: data.total || 0,
          };
          this.loadError = "";
          this.forbidden = "";

          if (keepSelection) {
            const stillThere = this.referrals.filter((r) => this.canSelect(r));
            this.selected = this.selected
              .map((s) => stillThere.find((r) => r.id === s.id))
              .filter(Boolean);
          } else {
            this.selected = [];
          }
          return res;
        })
        .catch((err) => {
          if (seq !== this.requestSeq) return;
          if (err.response && err.response.status === 403) {
            this.forbidden = errorMessage(err);
          } else {
            this.loadError = errorMessage(err, "Couldn't load job order referrals.");
          }
        })
        .finally(() => {
          if (seq !== this.requestSeq) return;
          this.loading = false;
          this.refreshing = false;
        });
    },
    refresh() {
      return this.fetchReferrals({ keepSelection: true });
    },
    setFilter(key) {
      if (this.filter === key) return;
      this.filter = key;
      this.pagination.current_page = 1;
      this.fetchReferrals();
    },
    changePerPage() {
      this.pagination.current_page = 1;
      this.fetchReferrals();
    },
    goToPage(page) {
      if (page < 1 || page > this.pagination.last_page) return;
      this.pagination.current_page = page;
      this.fetchReferrals();
    },

    canSelect(referral) {
      return referral.status !== "Closed";
    },
    isSelected(referral) {
      return this.selected.some((s) => s.id === referral.id);
    },
    toggleOne(referral, checked) {
      if (checked && !this.isSelected(referral)) {
        this.selected.push(referral);
      } else if (!checked) {
        this.selected = this.selected.filter((s) => s.id !== referral.id);
      }
    },
    toggleAll(checked) {
      this.selected = checked ? this.selectableRows.slice() : [];
    },
    isWaitingLong(referral) {
      return daysSince(referral.created_at) >= WAITING_LONG_DAYS;
    },

    completeSelected() {
      const referrals = this.selected.filter((r) => r.status !== "Closed");
      if (!referrals.length || this.busy) return;
      const count = referrals.length;
      const confirmed = window.confirm(
        `Mark ${count} ${count === 1 ? "referral" : "referrals"} as completed?\n\n` +
          "The matching tickets close too, and the requesters are notified. This can't be undone here."
      );
      if (!confirmed) return;

      this.busy = true;
      axios
        .post("api/support/job-order-referrals/close", { ids: referrals.map((r) => r.id) })
        .then((res) => {
          toastr.success(res.data.message, "Completed");
          this.selected = [];
          return this.fetchReferrals();
        })
        .catch((err) => {
          toastr.error(errorMessage(err), "Not completed");
        })
        .finally(() => {
          this.busy = false;
        });
    },
    openReferral(referral) {
      this.$refs.ViewJobOrderReferralDetailsModal.open(referral.id, referral);
    },
    print(referral) {
      this.$refs.JobOrderRequestTemplate.print(referral);
    },

    openFromRoute() {
      if (this.forbidden) return;
      const query = this.$route.query || {};
      if (query.referral) {
        this.$refs.ViewJobOrderReferralDetailsModal.open(query.referral);
        return;
      }
      if (query.ticket) {
        axios
          .get("api/support/job-order-referrals", {
            params: { status: "all", ticket_id: query.ticket, perPage: 5 },
          })
          .then((res) => {
            const referral = (res.data.data || [])[0];
            if (referral) this.openReferral(referral);
            else toastr.info(`No job order referral found for ticket #${query.ticket}.`);
          })
          .catch(() => {});
      }
    },
    clearReferralQuery() {
      const query = { ...(this.$route.query || {}) };
      if (!query.referral && !query.ticket && !query.view) return;
      delete query.referral;
      delete query.ticket;
      delete query.view;
      this.$router.replace({ query }).catch(() => {});
    },
  },
};
</script>
